import React, { useMemo } from "react";
import { theme } from "../../data/theme";
import { isGenerated } from "../../hooks/usePlanState";

/**
 * PlanPicker — the plan dropdown, in two groups.
 *
 * 🔴 GROUPED ON THE GENERATED FLAG, NOT ON THE NAME. `isGenerated` is the same
 * test "Delete all optimized" filters on, so what the picker calls disposable
 * and what the sweep will actually take are the same set BY CONSTRUCTION. A
 * name-prefix test would look identical today and drift the first time somebody
 * renamed a generated plan — and renaming one is how you say "actually I want
 * to keep this", which is exactly when the two must not disagree.
 *
 * Hand-built and generated plans used to interleave in one flat list, so a pile
 * of throwaway solves looked exactly like work. The grouping is what makes the
 * generated ones visibly disposable, which is what they are.
 *
 * 🔴 GROUPS ONLY WHEN BOTH SIDES EXIST. A lone "My plans" heading over every
 * option labels nothing — the information is the contrast, not the word — and
 * on a fresh league with one plan it would be pure chrome.
 *
 * 🔴 `compact` MAKES IT A CARET, AND THE NAME MOVES OUT. Once the plan's name
 * became editable in place (PlanNameField) it could not also be the face of
 * this control: clicking a name to rename it and clicking it to open a plan
 * list are two actions on one pixel, and about half the presses would do the
 * wrong one. So in compact mode the select renders as a switcher and nothing
 * else, immediately beside the name it used to display.
 *
 * The OPTIONS are untouched in both modes — same grouping, same order, same
 * flag — because that is the part being tested and the part that matters. The
 * native select is also kept as-is deliberately: it is one element, it is
 * keyboard- and screen-reader-correct for free, and a hand-rolled dropdown here
 * would be a third menu implementation in this toolbar.
 */
export default function PlanPicker({ plans = [], value, onChange, compact = false }) {
  const { mine, generated } = useMemo(() => ({
    mine: plans.filter((p) => !isGenerated(p)),
    generated: plans.filter(isGenerated),
  }), [plans]);

  /**
   * 🔴 EVERY OPTION CARRIES ITS OWN INK, and this is a bug fix rather than
   * defensive styling. Chromium's native option list INHERITS the select's
   * colour, and compact mode sets that colour to `transparent` to hide the
   * closed control's label. On Windows Chrome the dropdown therefore opened
   * tall and empty: sixteen rows of invisible text with one highlighted band
   * where the cursor was. The select stays transparent, because that is what
   * makes the caret-only control work; the list is told what it is instead.
   *
   * Explicit in BOTH modes on purpose. The non-compact select is already
   * text.primary, so this changes nothing there, and a rule that only applies
   * in the mode nobody tests by hand is a rule that comes back.
   */
  const optionStyle = {
    color: theme.colors.text.primary,
    background: theme.colors.bg.raised,
  };
  const groupStyle = {
    color: theme.colors.text.secondary,
    background: theme.colors.bg.raised,
  };

  const options = (list) => list.map((p) => (
    <option key={p.id} value={p.id} style={optionStyle}>{p.name}</option>
  ));

  const select = (
    <select
      data-testid="plan-picker"
      aria-label="Switch plan"
      title={compact ? "Switch plan" : undefined}
      value={value || ""}
      onChange={(e) => onChange && onChange(e.target.value)}
      style={{
        padding: compact ? `${theme.spacing.sm}px 0` : `${theme.spacing.sm}px ${theme.spacing.lg}px`,
        width: compact ? 34 : undefined,
        borderRadius: theme.radius.md,
        border: `1px solid ${theme.colors.border.default}`,
        background: theme.colors.bg.raised,
        // The selected option's text is hidden rather than the element being
        // narrowed around it: a 34px select still lays out its own label, and
        // clipping it would leave a sliver of the plan name showing beside the
        // full name next door.
        //
        // 🔴 THIS TRANSPARENT IS WHY EVERY OPTION BELOW SETS ITS OWN COLOUR.
        // Chromium's option list inherits it. See optionStyle.
        color: compact ? "transparent" : theme.colors.text.primary,
        textShadow: compact ? "none" : undefined,
        WebkitAppearance: compact ? "none" : undefined,
        MozAppearance: compact ? "none" : undefined,
        appearance: compact ? "none" : undefined,
        fontSize: theme.fontSize.sub,
        fontWeight: theme.fontWeight.bold,
        fontFamily: theme.fonts.body,
        cursor: "pointer",
      }}
    >
      {mine.length > 0 && generated.length > 0 ? (
        <>
          <optgroup label="My plans" style={groupStyle}>{options(mine)}</optgroup>
          <optgroup label="Optimized" style={groupStyle}>{options(generated)}</optgroup>
        </>
      ) : (
        options(plans)
      )}
    </select>
  );

  if (!compact) return select;

  // The caret is ours, drawn over the emptied select and transparent to the
  // pointer, so the whole 34px box still opens the native list.
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {select}
      <span
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
          color: theme.colors.text.secondary,
          fontSize: theme.fontSize.small,
        }}
      >▾</span>
    </span>
  );
}
